import { createSlice } from "@reduxjs/toolkit"

import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { albumSliceName } from "./album.constants.ts"
import {
  createAlbum,
  deleteAlbum,
  fetchAlbumFilterOptions,
  fetchAlbums,
  fetchMoreAlbums,
  getAlbum,
  removeAlbumAttachments,
  updateAlbum
} from "./album.thunks.ts"
import { type AlbumState } from "./album.types.ts"
import {
  getAttachments,
  getMoreAttachments,
  renameAttachment
} from "./attachments.thunks.ts"

const initialState: AlbumState = {
  albums: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  filters: { page: 1, page_size: 12, q: null },

  album: null,
  albumFilters: { page: 1, page_size: 20, q: null },
  albumFetchingFilterOptions: false,
  albumFilterOptions: {
    model_types: [],
    file_types: [],
    date_ranges: [],
    milestones: [],
    financial_items: [],
    invoices: [],
    checklist_items: []
  },

  attachments: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  attachmentFilters: { page: 1, page_size: 20, q: null },

  fetching: false,
  fetchingAlbum: false,
  fetchingAttachments: false,
  saveResult: null,
  saveFetching: false
}

const albumSlice = createSlice({
  name: albumSliceName,
  initialState,
  reducers: {
    albumSetProp: generateSetPropReducer<AlbumState>()
  },
  extraReducers: (builder) => {
    builder
      // fetchProject
      .addCase(fetchAlbums.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchAlbums.fulfilled, (state, action) => {
        state.fetching = false
        state.albums = action?.payload
      })
      .addCase(fetchAlbums.rejected, (state) => {
        state.fetching = false
        state.albums = initialState.albums
      })

      // fetchMoreAlbums
      .addCase(fetchMoreAlbums.fulfilled, (state, action) => {
        state.fetching = false
        state.albums = {
          ...action?.payload,
          results: [
            ...(state?.albums?.results || []),
            ...(action?.payload?.results || [])
          ]
        }
      })

      // getAlbum
      .addCase(getAlbum.pending, (state) => {
        state.fetchingAlbum = true
      })
      .addCase(getAlbum.fulfilled, (state, action) => {
        state.fetchingAlbum = false
        state.album = action?.payload
      })
      .addCase(getAlbum.rejected, (state) => {
        state.fetchingAlbum = false
        state.album = initialState.album
      })

      // updateAlbum
      .addCase(updateAlbum.fulfilled, (state, action) => {
        const updated = action?.payload
        // The grid can edit an album that is not the one open in the detail view.
        if (state.album?.id === updated?.id) {
          state.album = { ...state.album, ...updated }
        }

        const row = state.albums?.results?.find((a) => a.id === updated?.id)
        if (row) Object.assign(row, updated)
      })

      // // addAlbumAttachments
      // .addCase(addAlbumAttachments.fulfilled, (state, action) => {
      //   state.album = action?.payload
      // })

      // removeAlbumAttachments
      .addCase(removeAlbumAttachments.fulfilled, (state, action) => {
        state.album = action?.payload
      })

      // createAlbum
      .addCase(createAlbum.fulfilled, (state, action) => {
        state.fetching = false
        state.albums.results = [
          action?.payload,
          ...(state?.albums.results || [])
        ]
        state.albums.count = (state.albums.count || 0) + 1
      })

      // getAttachments
      .addCase(getAttachments.pending, (state) => {
        state.fetchingAttachments = true
      })
      .addCase(getAttachments.fulfilled, (state, action) => {
        state.fetchingAttachments = false
        // @todo here should be pagination
        state.attachments.results = action?.payload
      })
      .addCase(getAttachments.rejected, (state) => {
        state.fetchingAttachments = false
        state.attachments = initialState.attachments
      })

      // getMoreAttachments
      .addCase(getMoreAttachments.fulfilled, (state, action) => {
        state.fetchingAttachments = false
        state.attachments = {
          ...action?.payload,
          results: [
            ...(state?.attachments?.results || []),
            ...(action?.payload?.results || [])
          ]
        }
      })

      // renameAttachment
      .addCase(renameAttachment.fulfilled, (state, action) => {
        const renamed = action.payload
        if (!renamed?.id) return

        const listRow = state.attachments?.results?.find((a) => a.id === renamed.id)
        if (listRow) listRow.original_filename = renamed.original_filename

        const albumRow = state.album?.attachments?.find((a) => a.id === renamed.id)
        if (albumRow) albumRow.original_filename = renamed.original_filename
      })

      // fetchAlbumFilterOptions
      .addCase(fetchAlbumFilterOptions.pending, (state) => {
        state.albumFetchingFilterOptions = true
      })
      .addCase(fetchAlbumFilterOptions.fulfilled, (state, action) => {
        state.albumFilterOptions = action?.payload
        state.albumFetchingFilterOptions = false
      })
      .addCase(fetchAlbumFilterOptions.rejected, (state) => {
        state.albumFilterOptions = initialState.albumFilterOptions
        state.albumFetchingFilterOptions = false
      })
      .addCase(deleteAlbum.fulfilled, (state, action) => {
        state.albums.results = state.albums.results?.filter(
          (item) => item.id !== action.payload
        )
        state.albums.count -= 1
      })

      .addCase(logout.fulfilled, () => {
        console.log("album logout")
        return initialState
      })
  }
})
export const { albumSetProp } = albumSlice.actions

export default albumSlice
